/**
 * NavigationService - A centralized abstraction layer for window.location and window.history operations
 *
 * This service provides SSR-safe methods for navigation operations that can be easily mocked in Jest tests.
 * It handles cases where window is undefined (server-side rendering) and provides a consistent interface
 * for all navigation-related operations across the application.
 *
 * This abstraction is particularly important for Jest 30+ compatibility, where direct window.location
 * manipulation (like `delete window.location` or using Object.defineProperty to make it configurable)
 * is problematic due to stricter JSDOM handling.
 *
 * @module NavigationService
 *
 * @example
 * ```typescript
 * import NavigationService from './NavigationService';
 *
 * // Get current URL
 * const url = NavigationService.getCurrentUrl();
 *
 * // Navigate to a new page
 * NavigationService.navigate('/new-page');
 *
 * // Use History API
 * NavigationService.pushState({ page: 1 }, '', '/page/1');
 *
 * // In tests:
 * jest.spyOn(NavigationService, 'navigate').mockImplementation(() => {});
 * ```
 */
/**
 * NavigationService singleton instance providing navigation operations
 */
const NavigationService = {
    /**
     * Get the current full URL
     * @returns {string} The current URL (window.location.href) or empty string if window is undefined (SSR)
     */
    getCurrentUrl() {
        if (typeof window === 'undefined') {
            return '';
        }
        return window.location.href;
    },
    /**
     * Get the current pathname
     * @returns {string} The current pathname (window.location.pathname) or empty string if window is undefined (SSR)
     */
    getPathname() {
        if (typeof window === 'undefined') {
            return '';
        }
        return window.location.pathname;
    },
    /**
     * Get the current query string (including the leading '?')
     * @returns {string} The current search string (window.location.search) or empty string if window is undefined (SSR)
     */
    getSearch() {
        if (typeof window === 'undefined') {
            return '';
        }
        return window.location.search;
    },
    /**
     * Get the current hash fragment (including the leading '#')
     * @returns {string} The current hash (window.location.hash) or empty string if window is undefined (SSR)
     */
    getHash() {
        if (typeof window === 'undefined') {
            return '';
        }
        return window.location.hash;
    },
    /**
     * Get the origin of the current URL (protocol + hostname + port)
     * @returns {string} The origin (window.location.origin) or empty string if window is undefined (SSR)
     */
    getOrigin() {
        if (typeof window === 'undefined') {
            return '';
        }
        return window.location.origin;
    },
    /**
     * Get the current hostname
     * @returns {string} The current hostname (window.location.hostname) or empty string if window is undefined (SSR)
     */
    getHostname() {
        if (typeof window === 'undefined') {
            return '';
        }
        return window.location.hostname;
    },
    /**
     * Get the current protocol (e.g., 'http:' or 'https:')
     * @returns {string} The protocol (window.location.protocol) or empty string if window is undefined (SSR)
     */
    getProtocol() {
        if (typeof window === 'undefined') {
            return '';
        }
        return window.location.protocol;
    },
    /**
     * Get the current port
     * @returns {string} The port (window.location.port) or empty string if window is undefined (SSR)
     */
    getPort() {
        if (typeof window === 'undefined') {
            return '';
        }
        return window.location.port;
    },
    /**
     * Navigate to a new URL by setting window.location.href
     * @param {string} url - The URL to navigate to
     * @returns {void}
     */
    navigate(url) {
        if (typeof window !== 'undefined') {
            window.location.href = url;
        }
    },
    /**
     * Assign and Navigate to a new URL by setting window.location.assign
     * @param {string} url - The URL to navigate to
     * @returns {void}
     */
    assignAndNavigate(url) {
        if (typeof window !== 'undefined') {
            window.location.assign(url);
        }
    },
    /**
     * Reload the current page
     * @returns {void}
     */
    reload() {
        if (typeof window !== 'undefined') {
            window.location.reload();
        }
    },
    /**
     * Replace the current page in the browser history (no back button entry)
     * @param {string} url - The URL to replace with
     * @returns {void}
     */
    replace(url) {
        if (typeof window !== 'undefined') {
            window.location.replace(url);
        }
    },
    /**
     * Push a new entry onto the history stack
     * @param {any} state - A state object associated with the new history entry
     * @param {string} title - The title for the new history entry (currently ignored by most browsers)
     * @param {string} url - The URL for the new history entry
     * @returns {void}
     */
    pushState(state, title, url) {
        if (typeof window !== 'undefined' && window.history) {
            window.history.pushState(state, title, url);
        }
    },
    /**
     * Replace the current entry on the history stack
     * @param {any} state - A state object associated with the history entry
     * @param {string} title - The title for the history entry (currently ignored by most browsers)
     * @param {string} url - The URL for the history entry
     * @returns {void}
     */
    replaceState(state, title, url) {
        if (typeof window !== 'undefined' && window.history) {
            window.history.replaceState(state, title, url);
        }
    },
};
// Export individual methods for backward compatibility with existing imports (import * as NavigationService)
export const getCurrentUrl = () => NavigationService.getCurrentUrl();
export const getPathname = () => NavigationService.getPathname();
export const getSearch = () => NavigationService.getSearch();
export const getHash = () => NavigationService.getHash();
export const getOrigin = () => NavigationService.getOrigin();
export const getHostname = () => NavigationService.getHostname();
export const getProtocol = () => NavigationService.getProtocol();
export const getPort = () => NavigationService.getPort();
export const navigate = (url) => NavigationService.navigate(url);
export const assignAndNavigate = (url) => NavigationService.assignAndNavigate(url);
export const reload = () => NavigationService.reload();
export const replace = (url) => NavigationService.replace(url);
export const pushState = (state, title, url) => NavigationService.pushState(state, title, url);
export const replaceState = (state, title, url) => NavigationService.replaceState(state, title, url);
// Export as default for new imports (import NavigationService)
export default NavigationService;
